'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { systemAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const Navigation = ({ user, setUser }) => {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false); // New state for dashboard menu
  const [systemSettings, setSystemSettings] = useState(null);
  const isClientRef = useRef(false);

  useEffect(() => {
    // Set client flag to true when component mounts
    isClientRef.current = true;

    // Only access window on the client side
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        setScrolled(window.scrollY > 10);
      };
      window.addEventListener('scroll', handleScroll);
      
      // Listen for authChange event to update UI when auth state changes elsewhere
      const handleAuthChange = () => {
        // Refresh user data from localStorage to update the UI
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            if (setUser) {
              setUser(userData);
            }
          } catch (error) {
            console.error('Error parsing user data from localStorage:', error);
            if (setUser) {
              setUser(null);
            }
          }
        } else {
          if (setUser) {
            setUser(null);
          }
        }
      };
      
      window.addEventListener('authChange', handleAuthChange);
      
      // Also listen for storage events to handle cross-tab authentication changes
      const handleStorageChange = (e) => {
        if (e.key === 'user' || e.key === 'token') {
          handleAuthChange();
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('authChange', handleAuthChange);
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, []);

  useEffect(() => {
    // Fetch system settings to get logo
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const settings = await systemAPI.getSystemSettings();
      setSystemSettings(settings);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const { logout: contextLogout } = useAuth();
  
  const handleLogout = () => {
    // Show success toast
    if (typeof window !== 'undefined' && window.addToast) {
      window.addToast('You have been successfully logged out.', 'success');
    }

    // Use context logout function
    contextLogout();

    // Dispatch custom event to notify other components of auth change
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('authChange'));

      // Redirect to home page after logout
      router.push('/');
    }
  };

  // Toggle dashboard menu
  const toggleDashboardMenu = () => {
    setIsDashboardMenuOpen(!isDashboardMenuOpen);
  };

  // Close dashboard menu when clicking outside
  useEffect(() => {
    // Only run on client side
    if (!isClientRef.current) return;

    // Only access document on the client side
    if (typeof document !== 'undefined') {
      const handleClickOutside = (event) => {
        if (isDashboardMenuOpen && !event.target.closest('.dashboard-menu-container')) {
          setIsDashboardMenuOpen(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isDashboardMenuOpen]);

  // Get user role for navigation
  const userRole = user?.role || 'public';

  // Don't render anything that depends on window/document on the server
  const isClient = isClientRef.current;

  return (
    <nav className={`fixed w-full z-auto transition-all duration-300 print:hidden print:absolute print:top-0 ${scrolled ? 'bg-green-800 shadow-md py-3 md:py-4' : 'bg-green-800 py-4'}`} style={{ zIndex: 100 }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link href="/" className="text-xl md:text-2xl font-bold text-white flex items-center">
              {systemSettings && systemSettings.logoUrl ? (
                <><img
                  src={`${process.env.NEXT_PUBLIC_IMAGE_URL}${systemSettings.logoUrl}`}
                  alt={systemSettings.siteName || "Bus365"}
                  className="h-12 md:h-16 w-auto bg-white p-1 rounded-lg"
                /><span className="ml-2">{systemSettings.siteName}</span></>
              ) : (
                <>
                  <svg className="w-8 h-8 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{systemSettings?.siteName}</span>
                </>
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link href="/" className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">Home</Link>
            <Link href="/search" className={`text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium`}>
              Search Buses
            </Link>
            {/* <Link href="/blog" className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">Blog</Link> */}
            <Link href="/offers" className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">Offers</Link>
            <Link href="/feedback" className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">Feedback</Link>
            <Link href="/ticket-verification" className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">Track</Link>

            {user ? (
              <>
                <Link href={user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'provider' ? '/provider/dashboard' : user?.role === 'counter_manager' ? '/counter/dashboard' : user?.role === 'support' ? '/support/dashboard' : user?.role === 'customer_care' ? '/customer-care/manual-booking' : '/customer/dashboard'} className="text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium">
                  Dashboard
                </Link>
                {/* Dashboard menu with click toggle instead of hover */}
                {/* <div className="dashboard-menu-container relative">
                  <button
                    onClick={toggleDashboardMenu}
                    className="flex items-center text-sm md:text-base text-white hover:text-gray-200 transition-colors font-medium focus:outline-none"
                  >
                    <span className="mr-1">Dashboard</span>
                    <svg className={`w-4 h-4 transform transition-transform ${isDashboardMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {/* Show/hide menu based on state instead of hover */}
                {isClient && isDashboardMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    {userRole === 'customer' && (
                      <>
                        <Link href="/customer/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Profile</Link>
                        <Link href="/customer/bookings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Booking History</Link>
                      </>
                    )}
                    {userRole === 'provider' && (
                      <>
                        <Link href="/provider/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Dashboard</Link>
                        <Link href="/provider/buses" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">My Buses</Link>
                        <Link href="/provider/bookings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Bookings</Link>
                      </>
                    )}
                    {userRole === 'admin' && (
                      <>
                        <Link href="/admin/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Dashboard</Link>
                        <Link href="/admin/fleet" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Fleet Management</Link>
                        <Link href="/admin/providers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Providers</Link>
                      </>
                    )}
                  </div>
                )}
                {/* </div> */}
                <button
                  onClick={handleLogout}
                  className="bg-white text-green-800 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm md:text-base"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link href="/register" className="bg-white text-green-800 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm md:text-base">Register</Link>
                <Link href="/login" className="bg-white text-green-800 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm md:text-base">Login</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isClient && isMenuOpen && (
        <div className="md:hidden bg-[#16A34A] shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link href="/" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Home</Link>
            <Link href="/search" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Search Buses</Link>
            <Link href="/offers" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Offers</Link>
            <Link href="/feedback" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Feedback</Link>
            <Link href="/ticket-verification" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Track</Link>
            {user ? (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link href={user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'provider' ? '/provider/dashboard' : user?.role === 'counter_manager' ? '/counter/dashboard' : user?.role === 'support' ? '/support/dashboard' : user?.role === 'customer_care' ? '/customer-care/manual-booking' : '/customer/dashboard'} className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full text-left block px-3 py-2 rounded-md text-sm text-white hover:text-gray-200"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link href="/login" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Login</Link>
                <Link href="/register" className="block px-3 py-2 rounded-md text-sm md:text-base font-medium text-white hover:text-gray-200">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;